import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { TextWrap } from "./ui/TextWrap";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import CustomSelect from "./ui/CustomSelect";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const TaxRateLayer = () => {
  const [taxes, setTaxes] = useState([]);
  const [filteredTaxes, setFilteredTaxes] = useState([]);

  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [taxTypes, setTaxTypes] = useState([]);
  const [filteredTaxTypes, setFilteredTaxTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [taxId, setTaxId] = useState(null);
  const [selectedTax, setSelectedTax] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [countrySearchQuery, setCountrySearchQuery] = useState("");
  const [taxTypeSearchQuery, setTaxTypeSearchQuery] = useState("");
  const [taxTypeDropdownOpen, setTaxTypeDropdownOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [error, setError] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const { hasPermission } = usePermissions();

  const navigate = useNavigate();

  // Function to format created_at date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // Fallback to raw date if parsing fails
    }
  };

  const fetchTaxTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const queryParams = new URLSearchParams({
        want_pagination: 0,
        sort_by: "name",
        sort_order: "asc",
      });

      const response = await apiCall(
        "get",
        `/get-all-tax-types?${queryParams.toString()}`,
        null,
        navigate,
        true
      );

      if (response?.data?.success) {
        const taxTypeList = response.data.data || [];
        setTaxTypes(taxTypeList);
        setFilteredTaxTypes(taxTypeList);
        handleApiResponse(response.data, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to load tax types.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoading(true);
      let allCountries = [];
      let currentPage = 1;
      let lastPage = 1;

      do {
        const queryParams = new URLSearchParams({
          want_pagination: 0,
          sort_by: "country_name",
          sort_order: "asc",
        });

        const response = await apiCall(
          "get",
          `/get-all-countries?${queryParams.toString()}`,
          null,
          navigate
        );

        if (response?.data?.success) {
          const countriesData = response.data.data || [];
          allCountries = [...allCountries, ...countriesData];
          lastPage = response.data.pagination?.last_page || 1;
          currentPage += 1;
          handleApiResponse(response.data, "success", "GET");
        } else {
          handleCustomResponse("error", "Failed to load countries.");
          break;
        }
      } while (currentPage <= lastPage);

      setCountries(allCountries);
      setFilteredCountries(allCountries);
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxes = async (
    page = 1,
    perPage = 10,
    search = "",
    countryId = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page,
        per_page: perPage,
      });
      if (search) queryParams.append("search", encodeURIComponent(search));
      if (countryId) queryParams.append("country_id", countryId);
      if (sortBy) queryParams.append("sort_by", sortBy);
      if (sortOrder) queryParams.append("sort_order", sortOrder);

      const response = await apiCall(
        "get",
        `/get-all-taxes?${queryParams.toString()}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const taxList = response.data.data || [];
        const total = response.data.total || taxList.length;
        const currentPageFromResponse = response.data.current_page || page;
        const lastPage = response.data.last_page || Math.ceil(total / perPage);

        setTaxes(taxList);
        setFilteredTaxes(taxList);
        setTotalEntries(total);
        setTotalPages(parseInt(lastPage, 10));
        setCurrentPage(parseInt(currentPageFromResponse, 10));
        handleApiResponse(response.data, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to load taxes.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchTaxes = useCallback(
    debounce((page, perPage, search, countryId) => {
      fetchTaxes(
        page,
        perPage,
        search,
        countryId,
        sortConfig.field,
        sortConfig.direction
      );
    }, 300),
    [sortConfig]
  );

  const handleCountrySearch = (e) => {
    const value = e.target.value;
    setCountrySearchQuery(value);
    if (value) {
      const filtered = countries.filter((country) =>
        country.country_name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  };

  const handleTaxTypeSearch = (e) => {
    const value = e.target.value;
    setTaxTypeSearchQuery(value);
    if (value) {
      const filtered = taxTypes.filter((taxType) =>
        taxType.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredTaxTypes(filtered);
    } else {
      setFilteredTaxTypes(taxTypes);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedFetchTaxes(1, perPage, value, selectedCountryId);
  };

  const handleCountryFilterChange = (selected) => {
    const countryId = selected?.value || "";
    setSelectedCountryId(countryId);
    setCurrentPage(1);
    debouncedFetchTaxes(1, perPage, searchQuery, countryId);
  };

  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({
      field,
      direction: newDirection,
    });
    fetchTaxes(
      currentPage,
      perPage,
      searchQuery,
      selectedCountryId,
      field,
      newDirection
    );
  };

  const sortedTaxes = [...filteredTaxes].sort((a, b) => {
    if (!sortConfig.field) return 0;
    if (sortConfig.field === "created_at") {
      const dateA = new Date(a.created_at || "1970-01-01");
      const dateB = new Date(b.created_at || "1970-01-01");
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    const valueA = a[sortConfig.field] || "";
    const valueB = b[sortConfig.field] || "";
    if (sortConfig.field === "active") {
      const valA = valueA ? "Active" : "Inactive";
      const valB = valueB ? "Active" : "Inactive";
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    return sortConfig.direction === "asc"
      ? valueA.toString().localeCompare(valueB.toString())
      : valueB.toString().localeCompare(valueA.toString());
  });

 // YEH PEHLA ADD KARO ✅ - Initial load ke liye
useEffect(() => {
  fetchTaxTypes();
  fetchCountries();
  fetchTaxes(1, 10, "", "", null, "asc");
}, []); // Empty dependency

// YEH DUSRA ADD KARO ✅ - Page change ke liye
useEffect(() => {
  fetchTaxes(
    currentPage,
    perPage,
    searchQuery,
    selectedCountryId,
    sortConfig.field,
    sortConfig.direction
  );
}, [currentPage]); // Sirf currentPage

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(".dropdown")) {
        setDropdownOpen(false);
      }
      if (taxTypeDropdownOpen && !event.target.closest(".dropdown")) {
        setTaxTypeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen, taxTypeDropdownOpen]);

  const handleOpenModal = async (type, tax = null) => {
    setModalType(type);
    setShowModal(true);
    setTaxId(null);
    setSelectedTax(null);
    setCountrySearchQuery("");
    setTaxTypeSearchQuery("");
    setFilteredCountries(countries);
    setFilteredTaxTypes(taxTypes);
    setDropdownOpen(false);
    setTaxTypeDropdownOpen(false);

    if (type === "delete" && tax) {
      if (!tax.tax_id || isNaN(tax.tax_id)) {
        handleCustomResponse(
          "error",
          "Invalid or missing Tax ID for this tax."
        );
        return;
      }
      setSelectedTax(tax);
      setTaxId(tax.tax_id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setTaxId(null);
    setSelectedTax(null);
    setCountrySearchQuery("");
    setTaxTypeSearchQuery("");
    setFilteredCountries(countries);
    setFilteredTaxTypes(taxTypes);
  };

  const handleDeleteTax = async () => {
    if (!taxId || isNaN(taxId)) {
      handleCustomResponse("error", "Invalid or missing Tax ID.");
      return;
    }

    try {
      setSaveLoading(true);
      const response = await apiCall(
        "delete",
        `/delete-tax/${taxId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "DELETE");
        await fetchTaxes(currentPage, perPage, searchQuery, selectedCountryId);
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to delete tax.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "DELETE");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "delete") {
      handleDeleteTax();
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);
  if (error) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by tax type, Country"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={(value) => {
                setPerPage(value);
                setCurrentPage(1);
                fetchTaxes(1, value, searchQuery, selectedCountryId);
              }}
            />
            <div style={{ maxWidth: "200px" }}>
              <CustomSelect
                name="countryFilter"
                value={selectedCountryId}
                onChange={handleCountryFilterChange}
                options={countries}
                placeholder="Select Country"
                labelKey="country_name"
                valueKey="country_id"
                isSearchable={true}
                isClearable={true}
              />
            </div>
            <div className="ms-auto">
              {hasPermission("insert_tax_rates") && (
                <button
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                  onClick={() => navigate("/add-tax-rates")}
                >
                  <Icon icon="ri:add-line" className="me-1" /> Add Tax
                </button>
              )}
            </div>
          </div>

          {loading && filteredTaxes.length === 0 ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "300px" }}
            >
              <LoadingComponent />
            </div>
          ) : !loading && sortedTaxes.length === 0 ? (
            <TableDataNoAvailable message="No taxes found." />
          ) : (
            <div className="position-relative">
              <div
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <div className="table-responsive">
                  <table className="table bordered-table mb-0 table-hover">
                    <thead>
                      <tr
                        style={{ backgroundColor: "#475EAB", color: "white" }}
                      >
                        <th scope="col" className="p-2">
                          S.No
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("country_name")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Country</span>
                            <SortIcons
                              field="country_name"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("tax_type_name")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Tax Type</span>
                            <SortIcons
                              field="tax_type_name"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("tax_type_description")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Description</span>
                            <SortIcons
                              field="tax_type_description"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("tax_percent")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Tax Percent (%)</span>
                            <SortIcons
                              field="tax_percent"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("active")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex ">
                            <span>Status</span>
                            <SortIcons field="active" sortConfig={sortConfig} />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("created_at")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Created Date</span>
                            <SortIcons
                              field="created_at"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th scope="col" className="p-2">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTaxes.map((tax, index) => (
                        <tr key={tax.tax_id || index}>
                          <td className="p-2">
                            {(currentPage - 1) * perPage + index + 1}
                          </td>
                          <td className="p-2">{tax.country_name || "N/A"}</td>
                          <td className="p-2">
                            <span className="">
                              {tax.tax_type_name || "N/A"}
                            </span>
                          </td>
                          <td className="p-2">
                            <TextWrap
                              location={
                                tax.tax_type_description || "No description"
                              }
                            />
                          </td>
                          <td className="p-2">
                            {tax.tax_percent ? `${tax.tax_percent}%` : "N/A"}
                          </td>
                          <td className="p-2 ">
                            <span
                              className={`px-3 py-1 radius-4 fw-medium text-sm ${
                                tax.active
                                  ? "bg-success-focus text-success-600 border border-success-main"
                                  : "bg-danger-focus text-danger-600 border border-danger-main"
                              }`}
                            >
                              {tax.active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="p-2">{formatDate(tax.created_at)}</td>
                          <td className="p-2">
                            {hasPermission("update_tax_rates") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                onClick={() =>
                                  navigate(`/edit-tax-rates/${tax.tax_id}`)
                                }
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Edit";
                                  tooltip.style.cssText = `
                        position: absolute;
                        top: -35px;
                        left: 50%;
                        transform: translateX(-50%);
                        background-color: #333;
                        color: white;
                        padding: 5px 8px;
                        border-radius: 4px;
                        font-size: 12px;
                        white-space: nowrap;
                        z-index: 1000;
                        pointer-events: none;
                      `;
                                  tooltip.id = "edit-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("edit-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                              >
                                <Icon icon="lucide:edit" />
                              </button>
                            )}
                            {hasPermission("delete_tax_rates") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                onClick={() => handleOpenModal("delete", tax)}
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Delete";
                                  tooltip.style.cssText = `
                        position: absolute;
                        top: -35px;
                        left: 50%;
                        transform: translateX(-50%);
                        background-color: #333;
                        color: white;
                        padding: 5px 8px;
                        border-radius: 4px;
                        font-size: 12px;
                        white-space: nowrap;
                        z-index: 1000;
                        pointer-events: none;
                      `;
                                  tooltip.id = "delete-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("delete-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                              >
                                <Icon icon="mingcute:delete-2-line" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}

              <PaginationControls
                pagination={{
                  currentPage,
                  perPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete Tax</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete{" "}
                <strong>{selectedTax?.tax_type_name || "this tax"}</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleSubmit}
                disabled={saveLoading}
              >
                {saveLoading ? "Please wait..." : "Delete Tax"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxRateLayer;
